import React, { useState, useCallback } from 'react';
import SEO from './SEO';
import { Link } from 'react-router-dom';
import { useDownloadVersions } from '../hooks/useDownloadVersions';
import { trackDownload } from '../utils/analytics';
import './Downloads.css';

const ADS_EVENTS = {
  DOWNLOAD_INSTALLER: 'ads_conv_downloads_baixar_instalador',
  DOWNLOAD_DIRECT_LINK: 'ads_conv_downloads_link_direto',
  WHATSAPP_SUPPORT: 'ads_conv_downloads_whatsapp_suporte',
};

const Downloads = () => {
  const sendAdsEvent = useCallback((eventName, callback) => {
    if (window.gtag) {
      window.gtag('event', eventName, {
        'event_callback': callback,
        'event_timeout': 2000,
      });
    } else {
      callback();
    }
  }, []);

  const [selectedVersion, setSelectedVersion] = useState('latest');
  
  // Buscar dados da API
  const { data: downloadLinks, loading, error, refetch } = useDownloadVersions('VERITUXERP');

  /**
   * Manipula o download de uma versão específica
   * @param {string} version - 'latest' ou 'previous'
   */
  const handleDownload = useCallback((version) => {
    if (!downloadLinks || !downloadLinks[version] || !downloadLinks[version].url) {
      console.error('Versão de download não disponível:', version);
      return;
    }

    const link = downloadLinks[version];
    
    // Função callback para iniciar o download após o evento ser enviado
    const startDownload = () => {
      // Criar link de download
      const downloadLink = document.createElement('a');
      downloadLink.href = link.url;
      downloadLink.download = `Veritux-ERP-Setup-${link.version}.exe`;
      downloadLink.target = '_blank';
      downloadLink.rel = 'noopener noreferrer';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Analytics
      trackDownload(`Veritux ERP`, link.version);
    };

    // Enviar evento de conversão do Google Ads antes do download
    if (window.gtag) {
      sendAdsEvent(ADS_EVENTS.DOWNLOAD_INSTALLER, startDownload);
    } else {
      // Se gtag ainda não estiver disponível, iniciar download diretamente
      startDownload();
    }
  }, [downloadLinks, sendAdsEvent]);

  // Dados da versão selecionada
  const currentVersion = downloadLinks?.[selectedVersion] || null;

  return (
    <div className="downloads-page">
      <SEO
        title="Downloads - Veritux ERP | Instalador Desktop Windows"
        description="Baixe o instalador do Veritux ERP para Windows. Sistema completo de gestão empresarial para desktop. Requisitos mínimos e instruções de instalação."
        keywords="download Veritux ERP, instalador Windows, ERP desktop, baixar Veritux, software gestão empresarial"
        url="https://www.veritux.com.br/downloads"
      />

      {/* Header */}
      <header className="downloads-header">
        <div className="container">
          <div className="header-content">
            <Link to="/" className="logo">
              <img src="/Logo.ico" alt="Veritux ERP" className="logo-img" />
              <span className="logo-text">Veritux ERP</span>
            </Link>
            <nav className="nav">
              <Link to="/funcionalidades">Funcionalidades</Link>
              <Link to="/#precos">Preços</Link>
              <Link to="/downloads" className="active">Downloads</Link>
            </nav>
            <div className="header-cta">
              <Link to="/#demo" className="btn btn-primary">Teste Grátis</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="downloads-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-icon">💾</div>
            <h1 className="hero-title">Downloads</h1>
            <p className="hero-subtitle">
              Baixe o Veritux ERP Desktop para Windows
            </p>
            <p className="hero-description">
              Instale o sistema completo de gestão empresarial diretamente no seu computador. 
              Controle total, mesmo offline.
            </p>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="download-section">
        <div className="container">
          <div className="download-content">
            {loading && (
              <div className="download-loading">
                <div className="loading-spinner"></div>
                <p>Carregando informações de download...</p>
              </div>
            )}

            {error && (
              <div className="download-error">
                <div className="error-icon">⚠️</div>
                <h3>Erro ao carregar informações</h3>
                <p>Não foi possível carregar as informações de download. Por favor, tente novamente.</p>
                <button onClick={refetch} className="btn btn-primary">
                  Tentar Novamente
                </button>
              </div>
            )}

            {!loading && !error && downloadLinks && currentVersion && (
              <div className="download-card">
                <div className="download-header">
                  <div className="version-selector">
                    <h3>Versão para Download</h3>
                    <div className="version-tabs">
                      <button
                        className={`version-tab ${selectedVersion === 'latest' ? 'active' : ''}`}
                        onClick={() => setSelectedVersion('latest')}
                        disabled={!downloadLinks.latest}
                      >
                        <span className="version-label">Mais Recente</span>
                        <span className="version-number">
                          {downloadLinks.latest ? `v${downloadLinks.latest.version}` : 'N/A'}
                        </span>
                      </button>
                      <button
                        className={`version-tab ${selectedVersion === 'previous' ? 'active' : ''}`}
                        onClick={() => setSelectedVersion('previous')}
                        disabled={!downloadLinks.previous}
                      >
                        <span className="version-label">Versão Anterior</span>
                        <span className="version-number">
                          {downloadLinks.previous ? `v${downloadLinks.previous.version}` : 'N/A'}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="download-info">
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Versão</span>
                      <span className="info-value">v{currentVersion.version}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Tamanho</span>
                      <span className="info-value">{currentVersion.size || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Data de Lançamento</span>
                      <span className="info-value">
                        {currentVersion.releaseDate 
                          ? new Date(currentVersion.releaseDate).toLocaleDateString('pt-BR')
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Plataforma</span>
                      <span className="info-value">Windows 10/11</span>
                    </div>
                  </div>

                  <div className="download-actions">
                    <button
                      className="btn btn-primary btn-large download-btn"
                      onClick={() => handleDownload(selectedVersion)}
                      disabled={!currentVersion.url}
                    >
                      <span className="download-icon">⬇️</span>
                      Baixar Instalador
                    </button>
                    {currentVersion.url && (
                      <a
                        href={currentVersion.url}
                        className="btn btn-outline btn-large"
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        onClick={(e) => {
                          e.preventDefault();
                          const downloadUrl = currentVersion.url;
                          
                          // Função callback para iniciar o download após o evento ser enviado
                          const startDownload = () => {
                            const downloadLink = document.createElement('a');
                            downloadLink.href = downloadUrl;
                            downloadLink.download = `Veritux-ERP-Setup-${currentVersion.version}.exe`;
                            downloadLink.target = '_blank';
                            downloadLink.rel = 'noopener noreferrer';
                            document.body.appendChild(downloadLink);
                            downloadLink.click();
                            document.body.removeChild(downloadLink);
                          };

                          // Enviar evento de conversão do Google Ads antes do download
                          if (window.gtag) {
                            sendAdsEvent(ADS_EVENTS.DOWNLOAD_DIRECT_LINK, startDownload);
                          } else {
                            startDownload();
                          }
                        }}
                      >
                        <span className="download-icon">🔗</span>
                        Link Direto
                      </a>
                    )}
                  </div>

                  {currentVersion.checksum && (
                    <div className="checksum-info">
                      <p className="checksum-label">Checksum (SHA256):</p>
                      <code className="checksum-value">{currentVersion.checksum}</code>
                      <button
                        className="copy-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(currentVersion.checksum);
                          alert('Checksum copiado!');
                        }}
                      >
                        📋 Copiar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* System Requirements */}
      <section className="requirements-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Requisitos do Sistema</h2>
            <p className="section-subtitle">
              Certifique-se de que seu computador atende aos requisitos mínimos
            </p>
          </div>

          <div className="requirements-grid">
            <div className="requirement-card">
              <div className="requirement-icon">💻</div>
              <h3 className="requirement-title">Sistema Operacional</h3>
              <ul className="requirement-list">
                <li>Windows 10 (64-bit) ou superior</li>
                <li>Windows 11 (recomendado)</li>
                <li>Windows Server 2016 ou superior</li>
              </ul>
            </div>

            <div className="requirement-card">
              <div className="requirement-icon">⚙️</div>
              <h3 className="requirement-title">Processador</h3>
              <ul className="requirement-list">
                <li>Mínimo: Intel Core i3 ou equivalente</li>
                <li>Recomendado: Intel Core i5 ou superior</li>
                <li>Arquitetura: x64 (64-bit)</li>
              </ul>
            </div>

            <div className="requirement-card">
              <div className="requirement-icon">💾</div>
              <h3 className="requirement-title">Memória RAM</h3>
              <ul className="requirement-list">
                <li>Mínimo: 4 GB RAM</li>
                <li>Recomendado: 8 GB RAM ou mais</li>
                <li>Para múltiplos usuários: 16 GB RAM</li>
              </ul>
            </div>

            <div className="requirement-card">
              <div className="requirement-icon">📁</div>
              <h3 className="requirement-title">Espaço em Disco</h3>
              <ul className="requirement-list">
                <li>Mínimo: 2 GB de espaço livre</li>
                <li>Recomendado: 5 GB ou mais</li>
                <li>SSD recomendado para melhor performance</li>
              </ul>
            </div>

            <div className="requirement-card">
              <div className="requirement-icon">🌐</div>
              <h3 className="requirement-title">Conexão de Internet</h3>
              <ul className="requirement-list">
                <li>Requerida para instalação inicial</li>
                <li>Requerida para atualizações</li>
                <li>Para emissão de notas fiscais (NF-e/NFS-e/NFC-e)</li>
                <li>O sistema funciona localmente, sem conexão constante</li>
              </ul>
            </div>

            <div className="requirement-card">
              <div className="requirement-icon">🖥️</div>
              <h3 className="requirement-title">Resolução de Tela</h3>
              <ul className="requirement-list">
                <li>Mínimo: 1280x720 pixels</li>
                <li>Recomendado: 1920x1080 pixels ou superior</li>
                <li>Suporte a múltiplos monitores</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Instructions */}
      <section className="instructions-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Instruções de Instalação</h2>
            <p className="section-subtitle">
              Siga estes passos para instalar o Veritux ERP em seu computador
            </p>
          </div>

          <div className="instructions-steps">
            <div className="instruction-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3 className="step-title">Baixe o Instalador</h3>
                <p className="step-text">
                  Clique no botão "Baixar Instalador" acima para iniciar o download do arquivo .exe.
                  O download pode levar alguns minutos dependendo da sua conexão.
                </p>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3 className="step-title">Execute o Instalador</h3>
                <p className="step-text">
                  Localize o arquivo baixado (geralmente na pasta Downloads) e execute-o.
                  Se o Windows solicitar permissão de administrador, clique em "Sim".
                </p>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3 className="step-title">Siga o Assistente de Instalação</h3>
                <p className="step-text">
                  O assistente de instalação irá guiá-lo através do processo.
                  Escolha o diretório de instalação (ou use o padrão) e clique em "Instalar".
                </p>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3 className="step-title">Aguarde a Instalação</h3>
                <p className="step-text">
                  A instalação pode levar de 5 a 20 minutos. Não feche a janela durante o processo.
                </p>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3 className="step-title">Inicie o Veritux ERP</h3>
                <p className="step-text">
                  Após a instalação, o Veritux ERP será iniciado automaticamente.
                  Você também pode encontrá-lo no menu Iniciar do Windows.
                </p>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">6</div>
              <div className="step-content">
                <h3 className="step-title">Faça Login</h3>
                <p className="step-text">
                  Use suas credenciais de conta Veritux ERP para fazer login.
                  Se você ainda não tem uma conta, <Link to="/#demo">crie uma gratuitamente</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="troubleshooting-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Problemas Comuns</h2>
            <p className="section-subtitle">
              Soluções para problemas frequentes durante a instalação
            </p>
          </div>

          <div className="troubleshooting-grid">
            <div className="troubleshooting-item">
              <h3 className="troubleshooting-title">⚠️ Windows bloqueia o download</h3>
              <p className="troubleshooting-text">
                Se o Windows Defender ou SmartScreen bloquear o download, clique em "Mais informações" 
                e depois em "Executar mesmo assim". O arquivo é seguro e assinado digitalmente.
              </p>
            </div>

            <div className="troubleshooting-item">
              <h3 className="troubleshooting-title">❌ Erro durante a instalação</h3>
              <p className="troubleshooting-text">
                Certifique-se de que tem espaço suficiente em disco e permissões de administrador.
                Tente executar o instalador como administrador (clique com botão direito → Executar como administrador).
              </p>
            </div>

            <div className="troubleshooting-item">
              <h3 className="troubleshooting-title">🔄 Atualização não funciona</h3>
              <p className="troubleshooting-text">
                Feche completamente o Veritux ERP antes de instalar uma nova versão.
                Se o problema persistir, desinstale a versão anterior e instale a nova versão.
              </p>
            </div>

            <div className="troubleshooting-item">
              <h3 className="troubleshooting-title">🌐 Problemas de conexão</h3>
              <p className="troubleshooting-text">
                Verifique sua conexão com a internet e firewall. O Veritux ERP funciona localmente,
                mas precisa de conexão para instalação, atualizações e emissão de notas fiscais.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="downloads-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Precisa de Ajuda?</h2>
            <p className="cta-subtitle">
              Nossa equipe de suporte está pronta para ajudar você com a instalação
            </p>
            <div className="cta-buttons">
              <a
                href={`https://wa.me/5543996782197?text=${encodeURIComponent('Olá! Preciso de ajuda com a instalação do Veritux ERP.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-large"
                onClick={(e) => {
                  e.preventDefault();
                  const whatsappUrl = `https://wa.me/5543996782197?text=${encodeURIComponent('Olá! Preciso de ajuda com a instalação do Veritux ERP.')}`;
                  if (window.gtagSendEvent) {
                    window.gtagSendEvent(whatsappUrl, ADS_EVENTS.WHATSAPP_SUPPORT);
                  } else if (window.gtag) {
                    sendAdsEvent(ADS_EVENTS.WHATSAPP_SUPPORT, () => {
                      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
                    });
                  } else {
                    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                Falar com Suporte
              </a>
              <Link to="/#ajuda" className="btn btn-secondary btn-large">
                Central de Ajuda
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="downloads-footer">
        <div className="container">
          <div className="footer-content">
            <p>&copy; 2026 Veritux ERP. Todos os direitos reservados.</p>
            <div className="footer-links">
              <Link to="/">Voltar ao Início</Link>
              <Link to="/#precos">Preços</Link>
              <Link to="/funcionalidades">Funcionalidades</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>      
  );
};

export default Downloads;
